import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useKeycloak } from 'keycloak-react-web';
import { hasStaffSignal } from '../../app/roles';
import { usePermissions } from '../permissions/usePermissions';
import { openSnackbar } from '../snackbar/snackbarSlice';
import { syncFavourites } from './favouritesSlice';
import { PortalRoutesContext } from './PortalRoutesContext';
import { pageMetadata } from './pageMetadata';

// Keep error handling outside components, including the account check after awaiting a request.
const saveFavourites = (request) => async (dispatch, getState) => {
  const result = await dispatch(syncFavourites(request));
  const state = getState().favourites;
  if (state.owner !== request.owner || state.requestId !== result.meta.requestId || result.meta.condition) return result;
  if (syncFavourites.rejected.match(result)) {
    dispatch(openSnackbar(result.payload?.message || 'Unable to save favourites.'));
    if ([404, 409].includes(result.payload?.status)) dispatch(syncFavourites({ owner: request.owner }));
  }
  return result;
};

export const useFavourites = () => {
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { can, settled } = usePermissions();
  const routes = useContext(PortalRoutesContext);
  const state = useSelector((store) => store.favourites);
  const owner = keycloak?.authenticated && hasStaffSignal(keycloak) ? keycloak.tokenParsed?.sub : null;
  const items = owner && state.owner === owner ? state.items : [];
  const busy = state.status === 'loading';
  const ready = Boolean(owner && state.owner === owner && state.status === 'succeeded' && settled);
  const metadata = (path) => settled ? pageMetadata(path, routes, keycloak, can) : null;
  const save = (operation = 'load', body = {}) => {
    if (!owner || busy || (operation !== 'load' && !ready)) return Promise.resolve();
    return dispatch(saveFavourites({ ...body, owner, operation }));
  };
  return {
    owner, items, busy, ready, status: state.owner === owner ? state.status : 'idle',
    metadata,
    visible: items.filter((item) => metadata(item.path)),
    isPinned: (path) => {
      const current = metadata(path);
      return Boolean(current && items.some((item) => item.path === current.path));
    },
    togglePin: (page) => {
      const current = metadata(page.path);
      if (!current) return Promise.resolve();
      const saved = items.find((item) => item.path === current.path);
      return saved ? save('remove', { id: saved.id }) : save('pin', { ...current, label: page.label || current.label });
    },
    save,
  };
};
